import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { NumberParam, StringParam, useQueryParams } from 'use-query-params'

import { getConversations } from '../../api/conversations'
import useApiCall from '../../api/useApiCall'
import { DashArrayParam } from '../../utils/useQueryParams'
import LoadingSection from '../LoadingSection/LoadingSection'
import NotFound from '../NotFound/NotFound'

import Conversation from './Conversation/Conversation'
import { ConversationsFiltersEnum } from './ConversationsNav/ConversationsFilters/ConversationsFilters'
import ConversationsNav from './ConversationsNav/ConversationsNav'

import styles from './conversations.module.scss'

export interface IConversations {
    id: string
    message: string
    num_messages: number
    timestamp: number
}

function Conversations() {
    const { conversationId } = useParams()

    const navigate = useNavigate()

    const { t } = useTranslation()

    const [query] = useQueryParams({
        [ConversationsFiltersEnum.ENTITIES]: DashArrayParam,
        [ConversationsFiltersEnum.INTENTS]: DashArrayParam,
        [ConversationsFiltersEnum.ACTIONS]: DashArrayParam,
        [ConversationsFiltersEnum.DATE_FROM]: StringParam,
        [ConversationsFiltersEnum.DATE_TO]: StringParam,
        [ConversationsFiltersEnum.CONVERSATION_LENGTH]: NumberParam,
    })

    const [pagination, setPagination] = useState(0)
    const [conversations, setConversations] = useState<IConversations[]>([])
    const [areAllConversationsFetched, setAreAllConversationsFetched] =
        useState(false)

    const [{ loading }, { refetch: refetchConversations }] = useApiCall(
        getConversations,
        handleGetConversations,
        pagination,
        query || ''
    )

    useEffect(() => {
        setPagination(0)
    }, [query])

    function handleGetConversations(conversations: IConversations[]) {
        if (!conversationId) {
            const [first] = conversations
            if (!first) return
            navigate(first.id)
        }

        if (pagination === 0) {
            setAreAllConversationsFetched(false)
            setConversations(conversations)

            if (conversations.length < 10) {
                setAreAllConversationsFetched(true)
            }

            return
        }

        if (conversations.length < 10) {
            setAreAllConversationsFetched(true)
        }

        setConversations((oldConversations) => [
            ...oldConversations,
            ...conversations,
        ])
    }

    if (loading) {
        return <LoadingSection />
    }

    return (
        <main className={styles.conversations}>
            <ConversationsNav
                conversations={conversations}
                setPagination={setPagination}
                className={styles.conversations__nav}
                areAllConversationsFetched={areAllConversationsFetched}
                refetchConversations={refetchConversations}
            />
            {conversationId ? (
                <Conversation className={styles.conversations__conversation} />
            ) : (
                <section className={styles.conversations__conversation}>
                    <NotFound title={t('no_conversations')} />
                </section>
            )}
        </main>
    )
}

export default Conversations
